import { type LucideIcon, TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: number | string;
  icon: LucideIcon;
  trend?: {
    value: number;
    label: string;
  };
  loading?: boolean;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
}

const colorMap = {
  blue: {
    bg: 'bg-blue-500/10',
    icon: 'text-blue-400',
    trend: 'text-blue-400',
  },
  green: {
    bg: 'bg-green-500/10',
    icon: 'text-green-400',
    trend: 'text-green-400',
  },
  yellow: {
    bg: 'bg-yellow-500/10',
    icon: 'text-yellow-400',
    trend: 'text-yellow-400',
  },
  red: {
    bg: 'bg-red-500/10',
    icon: 'text-red-400',
    trend: 'text-red-400',
  },
  purple: {
    bg: 'bg-purple-500/10',
    icon: 'text-purple-400',
    trend: 'text-purple-400',
  },
};

export function MetricCard({
  title,
  value,
  icon: Icon,
  trend,
  loading = false,
  color = 'blue',
}: MetricCardProps) {
  const colors = colorMap[color];

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 hover:border-slate-700 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-400">{title}</p>
          {loading ? (
            <div className="mt-2 h-8 flex items-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            </div>
          ) : (
            <p className="mt-2 text-3xl font-bold text-white">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colors.bg}`}>
          <Icon className={`h-6 w-6 ${colors.icon}`} />
        </div>
      </div>

      {trend && !loading && (
        <div className="mt-4 flex items-center gap-2">
          {trend.value > 0 ? (
            <TrendingUp className="h-4 w-4 text-green-400" />
          ) : trend.value < 0 ? (
            <TrendingDown className="h-4 w-4 text-red-400" />
          ) : (
            <Minus className="h-4 w-4 text-slate-400" />
          )}
          <span
            className={`text-sm font-medium ${
              trend.value > 0
                ? 'text-green-400'
                : trend.value < 0
                ? 'text-red-400'
                : 'text-slate-400'
            }`}
          >
            {trend.value > 0 ? '+' : ''}
            {trend.value}%
          </span>
          <span className="text-sm text-slate-500">{trend.label}</span>
        </div>
      )}
    </div>
  );
}
